import { Dispatch } from "react";
import axios, { AxiosResponse } from "axios";

import {
  Env,
  Module,
  EndPoint,
  getApiCall,
  postApiCall,
  deleteApiCall,
} from "../../../common/constants";
import {
  setMySubscriptionDetails,
  setSubscriptionlistingData,
  setSelectedSubscriptionDetails,
} from "./slice";
import {
  showAlert,
  getAccessToken,
  isAPISuccessful,
  checkInternetAndShowAlert,
} from "../../../common/utils";
import {
  AnalyticsEvents,
  capturePosthogEvent,
} from "../../../common/utils/analytics";
import { setLoading } from "../../../redux/global.slice";
import { getMyProfileData } from "../../my-profile/services/action";
import { getTimezone } from "../../../common/constants/axios.instance";
import { VERIFICATION_POPUP_TYPE } from "../../../common/constants/enum";
import { apiSuccessCode } from "../../../common/constants/axios.instance";

export const getSubscriptionListing = () => (dispatch: Dispatch<any>) => {
  // Check for network connectivity
  if (!checkInternetAndShowAlert(showAlert)) {
    return;
  }
  dispatch(setLoading(true));

  getApiCall(
    Module.manufacturer,
    `${EndPoint.subscriptionPlan}`,
    (response: AxiosResponse<any>) => {
      const { statusCode, data } = response.data;

      if (statusCode === apiSuccessCode.success) {
        dispatch(setSubscriptionlistingData(data));
      }
      dispatch(setLoading(false));
    },
    (error: any) => {
      dispatch(setLoading(false));
      showAlert(2, error.message);
    },
  );
};

export const onBuySubscription =
  (
    values: {
      planId: string;
      userBankAccountId: string;
      subscriptionOfferId: string;
    },
    userId: string,
    callback: (
      userType: string,
      permissions: any,
      isSubscription: boolean,
    ) => void,
  ) =>
  (dispatch: Dispatch<any>) => {
    if (!navigator.onLine) {
      showAlert(2, "No internet connection!");
      return;
    }

    dispatch(setLoading(true));
    const token = getAccessToken();

    const config = {
      method: "post",
      url: Env.BASE_URL + Module.manufacturer + EndPoint.buySubscription,
      headers: {
        platform: 1,
        "X-API-Key": Env.API_KEY,
        "accept-language": "en",
        Accept: "application/json",
        timezone: getTimezone(),
        "Content-Type": "application/json",
        "Access-Control-Allow-Origin": "*",
        ...(token && { Authorization: `Bearer ${token}` }),
      },
      data: values,
    };

    axios(config)
      .then((response: AxiosResponse<any>) => {
        const { data } = response;
        if (data?.statusCode === apiSuccessCode.postApiSuccess) {
          capturePosthogEvent(AnalyticsEvents.SUBSCRIPTION_PURCHASED, {
            user_id: userId,
            plan_id: values.planId,
            user_bank_account_id: values.userBankAccountId,
          });
          showAlert(1, data.message);
          dispatch(getMyProfileData(userId, true, callback));
        }
        dispatch(setLoading(false));
      })
      .catch((err) => {
        dispatch(setLoading(false));
        showAlert(2, err?.message);
      });
  };

export const getSelectedSubscriptionDetails =
  (planId: string, callback?: () => void) => (dispatch: Dispatch<any>) => {
    // Check for network connectivity
    if (!checkInternetAndShowAlert(showAlert)) {
      return;
    }
    dispatch(setLoading(true));

    getApiCall(
      Module.manufacturer,
      `${EndPoint.subscription}/${planId}/checkout`,
      (response: AxiosResponse<any>) => {
        const { statusCode, data } = response.data;

        if (statusCode === apiSuccessCode.success) {
          dispatch(setSelectedSubscriptionDetails(data));

          if (callback) callback();
        }
        dispatch(setLoading(false));
      },
      (error: any) => {
        dispatch(setLoading(false));
        showAlert(2, error.message);
      },
    );
  };

export const onCancelSubscription =
  (subscriptionToken: string, callback?: () => void) =>
  (dispatch: Dispatch<any>) => {
    // Check for network connectivity
    if (!checkInternetAndShowAlert(showAlert)) {
      return;
    }
    dispatch(setLoading(true));

    deleteApiCall(
      Module.manufacturer + `${EndPoint.subscription}/${subscriptionToken}`,
      "",
      (respData: AxiosResponse<any>) => {
        const { data } = respData;
        if (isAPISuccessful(data?.statusCode)) {
          showAlert(1, data.message);
          if (callback) callback();
        }
        dispatch(setLoading(false));
      },
      (error: any) => {
        dispatch(setLoading(false));
        showAlert(2, error.message);
      },
    );
  };

export const onFreeTrailSubscription =
  (
    userId: string,
    callback: (
      userType: string,
      permissions: any,
      isSubscription: boolean,
    ) => void,
  ) =>
  (dispatch: Dispatch<any>) => {
    // Check for network connectivity
    if (!checkInternetAndShowAlert(showAlert)) {
      return;
    }
    dispatch(setLoading(true));

    postApiCall(
      Module.manufacturer + EndPoint.freeTrialSubscription,
      {},
      (respData: AxiosResponse<any>) => {
        const { data } = respData;
        if (data?.statusCode === apiSuccessCode.postApiSuccess) {
          showAlert(1, data.message);
          dispatch(getMyProfileData(userId, true, callback));
        }
        dispatch(setLoading(false));
      },
      (error: any) => {
        dispatch(setLoading(false));
        showAlert(2, error.message);
      },
    );
  };

export const getMySubscriptionDetails =
  (subscriptionToken: string) => (dispatch: Dispatch<any>) => {
    // Check for network connectivity
    if (!checkInternetAndShowAlert(showAlert)) {
      return;
    }
    dispatch(setLoading(true));

    getApiCall(
      Module.manufacturer,
      `${EndPoint.subscription}/${subscriptionToken}`,
      (response: AxiosResponse<any>) => {
        const { statusCode, data } = response.data;

        if (statusCode === apiSuccessCode.success) {
          dispatch(setMySubscriptionDetails(data));
        }
        dispatch(setLoading(false));
      },
      (error: any) => {
        dispatch(setLoading(false));
        showAlert(2, error.message);
      },
    );
  };

export const onVerifyBillingEmail =
  (payload: any, verificationPopupType?: string, callback?: () => void) =>
  (dispatch: Dispatch<any>) => {
    // Check for network connectivity
    if (!checkInternetAndShowAlert(showAlert)) {
      return;
    }
    dispatch(setLoading(true));

    postApiCall(
      Module.manufacturer + EndPoint.verifyBillingEmail,
      payload,
      (respData: AxiosResponse<any>) => {
        const { data } = respData;
        if (data?.statusCode === apiSuccessCode.success) {
          if (verificationPopupType === VERIFICATION_POPUP_TYPE.UPDATE) {
            showAlert(1, "Billing Email updated succesfully");
          } else if (verificationPopupType === "RESEND") {
            showAlert(1, "Verification Code has been resent successfully");
          } else {
            showAlert(1, data.message);
          }
          if (callback) callback();
        }
        dispatch(setLoading(false));
      },
      (error: any) => {
        dispatch(setLoading(false));
        showAlert(2, error.message);
      },
    );
  };

export const verifyBillingEmailOtp =
  (payload: { otp: string }, callback?: () => void) =>
  (dispatch: Dispatch<any>) => {
    // Check for network connectivity
    if (!checkInternetAndShowAlert(showAlert)) {
      return;
    }

    dispatch(setLoading(true));

    postApiCall(
      Module.manufacturer + EndPoint.verifyBillingEmailOtp,
      payload,
      (respData: AxiosResponse<any>) => {
        const { data } = respData;
        if (data?.statusCode === apiSuccessCode.success) {
          showAlert(1, data.message);
          if (callback) callback();
        }
        dispatch(setLoading(false));
      },
      (error: any) => {
        dispatch(setLoading(false));
        showAlert(2, error.message);
      },
    );
  };

export const getAeropayToken = (callback: any) => (dispatch: Dispatch<any>) => {
  // Check for network connectivity
  if (!checkInternetAndShowAlert(showAlert)) {
    return;
  }
  dispatch(setLoading(true));

  getApiCall(
    Module.manufacturer,
    EndPoint.generateAeropayToken,
    (response: AxiosResponse<any>) => {
      const { statusCode, data } = response.data;

      if (statusCode === apiSuccessCode.success) {
        const token = data?.token;
        callback(token);
      }
      dispatch(setLoading(false));
    },
    (error: any) => {
      dispatch(setLoading(false));
      showAlert(2, error.message);
    },
  );
};

export const linkAeropayAccount =
  (
    payload: {
      userId: string;
      userPassword: string;
    },
    callback?: () => void,
  ) =>
  (dispatch: Dispatch<any>) => {
    // Check for network connectivity
    if (!checkInternetAndShowAlert(showAlert)) {
      return;
    }
    dispatch(setLoading(true));

    postApiCall(
      Module.manufacturer + EndPoint.linkAeropayAcccount,
      payload,
      (respData: AxiosResponse<any>) => {
        const { data } = respData;
        if (isAPISuccessful(data?.statusCode)) {
          showAlert(1, data.message);
          if (callback) callback();
        }
      },
      (error: any) => {
        dispatch(setLoading(false));
        showAlert(2, error.message);
      },
    );
  };

export const onByPassSubscription =
  (userId: string, callback?: any) => (dispatch: Dispatch<any>) => {
    // Check for network connectivity
    if (!checkInternetAndShowAlert(showAlert)) {
      return;
    }
    dispatch(setLoading(true));

    postApiCall(
      Module.manufacturer + EndPoint.freeTrialBypass,
      {},
      (respData: AxiosResponse<any>) => {
        const { data } = respData;
        if (data?.statusCode === apiSuccessCode.postApiSuccess) {
          showAlert(1, data.message);
          dispatch(getMyProfileData(userId, true, callback));
        }
        dispatch(setLoading(false));
      },
      (error: any) => {
        dispatch(setLoading(false));
        showAlert(2, error.message);
      },
    );
  };
